/**
 * Calcul des délais d'une demande d'accompagnement, en HEURES OUVRÉES.
 *
 * Deux délais suivis :
 *   1. attribution   = assignedAt − createdAt
 *   2. prise en charge = acceptedAt − assignedAt
 *
 * Heures ouvrées = lun–ven, 09:00–18:00 (config simple, ajustable). On compte
 * en millisecondes ouvrées ; une « journée ouvrée » vaut WORK_HOURS_PER_DAY h
 * pour le formatage (« 2 j 3 h »). Aucune dépendance externe nécessaire.
 */

const WORK_START_HOUR = 9
const WORK_END_HOUR = 18
const WORK_HOURS_PER_DAY = WORK_END_HOUR - WORK_START_HOUR // 9 h

function isWorkday(d: Date): boolean {
  const day = d.getDay() // 0 = dimanche … 6 = samedi
  return day >= 1 && day <= 5
}

/**
 * Millisecondes ouvrées entre deux instants ISO. Retourne 0 si dates absentes,
 * incohérentes (fin ≤ début) ou hors plage ouvrée.
 */
export function businessMsBetween(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): number | null {
  if (!startIso || !endIso) return null
  const start = new Date(startIso)
  const end = new Date(endIso)
  const startMs = start.getTime()
  const endMs = end.getTime()
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return 0

  let total = 0
  const cursor = new Date(start)
  cursor.setHours(0, 0, 0, 0)

  while (cursor.getTime() <= endMs) {
    if (isWorkday(cursor)) {
      const windowStart = new Date(cursor)
      windowStart.setHours(WORK_START_HOUR, 0, 0, 0)
      const windowEnd = new Date(cursor)
      windowEnd.setHours(WORK_END_HOUR, 0, 0, 0)

      const from = Math.max(startMs, windowStart.getTime())
      const to = Math.min(endMs, windowEnd.getTime())
      if (to > from) total += to - from
    }
    cursor.setDate(cursor.getDate() + 1)
  }
  return total
}

/** Délai d'attribution (création → attribution), heures ouvrées. */
export function assignDelayMs(req: {
  createdAt: string
  assignedAt: string | null
}): number | null {
  return businessMsBetween(req.createdAt, req.assignedAt)
}

/** Délai de prise en charge (attribution → prise en charge), heures ouvrées. */
export function acceptDelayMs(req: {
  assignedAt: string | null
  acceptedAt: string | null
}): number | null {
  return businessMsBetween(req.assignedAt, req.acceptedAt)
}

/**
 * Temps de traitement (ouverture → fermeture), heures ouvrées.
 * `null` tant que la demande n'est pas clôturée/annulée (closedAt absent).
 */
export function resolveDelayMs(req: {
  createdAt: string
  closedAt: string | null
}): number | null {
  return businessMsBetween(req.createdAt, req.closedAt)
}

/** Cumul du temps passé saisi sur les actions, en minutes (effort réel). */
export function totalTimeSpentMinutes(req: {
  actions: Array<{ timeSpentMinutes: number | null }>
}): number {
  return req.actions.reduce((sum, a) => sum + (a.timeSpentMinutes ?? 0), 0)
}

/**
 * Formate un nombre de minutes d'effort en « 1 h 30 » / « 45 min ».
 * Contrairement à `formatDuration` (délais), pas de notion de « jour ouvré » :
 * le temps passé est un cumul d'effort, exprimé en heures/minutes.
 */
export function formatMinutes(min: number): string {
  if (min <= 0) return '0 min'
  const h = Math.floor(min / 60)
  const m = min % 60
  if (h && m) return `${h} h ${String(m).padStart(2, '0')}`
  if (h) return `${h} h`
  return `${m} min`
}

/**
 * Formate une durée (ms ouvrées) en « 2 j 3 h » / « 3 h 15 min » / « 45 min ».
 * `null` → « — » (délai non encore mesurable).
 */
export function formatDuration(ms: number | null): string {
  if (ms == null) return '—'
  const minutes = Math.round(ms / 60_000)
  if (minutes <= 0) return '0 min'
  if (minutes < 60) return `${minutes} min`

  const totalHours = Math.floor(minutes / 60)
  const remMinutes = minutes % 60

  if (totalHours < WORK_HOURS_PER_DAY) {
    return remMinutes ? `${totalHours} h ${remMinutes} min` : `${totalHours} h`
  }

  const days = Math.floor(totalHours / WORK_HOURS_PER_DAY)
  const hours = totalHours % WORK_HOURS_PER_DAY
  return hours ? `${days} j ${hours} h` : `${days} j`
}

/** Moyenne (en ms) d'une liste de délais en ignorant les `null`. `null` si vide. */
export function averageMs(values: Array<number | null>): number | null {
  const nums = values.filter((v): v is number => v != null)
  if (!nums.length) return null
  return Math.round(nums.reduce((sum, v) => sum + v, 0) / nums.length)
}

/** SLA prise en charge : au-delà de N heures ouvrées après l'attribution → « en retard ». */
export const ACCOMP_TAKE_SLA_BUSINESS_HOURS = 48

/** ms ouvrées écoulées depuis l'attribution tant que la demande attend sa prise en charge. */
export function takeChargeWaitingMs(req: {
  status: string
  assignedAt: string | null
  acceptedAt: string | null
}): number | null {
  if (req.status !== 'ATTRIBUEE' || !req.assignedAt || req.acceptedAt) return null
  return businessMsBetween(req.assignedAt, new Date().toISOString())
}

/** Vrai si la prise en charge dépasse le SLA (48h ouvrées) après l'attribution. */
export function isTakeChargeOverdue(req: {
  status: string
  assignedAt: string | null
  acceptedAt: string | null
}): boolean {
  const ms = takeChargeWaitingMs(req)
  return ms != null && ms > ACCOMP_TAKE_SLA_BUSINESS_HOURS * 3_600_000
}
